import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box, Grid, IconButton, Link, makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';
import _ from 'lodash';
import { useTheme } from '../../context/theme/theme';
import CustomModal from '../common/Modal';
import Button from '../common/Button';
import {
  DEFAULT_PHONE_NUMBER_LENGTH,
  MINIMAL_PHONE_NUMBER_VALIDATE_LENGTH,
  MY_PHONE_NUMBER_LENGTH,
  validatePhoneNumber,
  validateThaiNumber,
} from '../../utils/Validator/PhonNumber';
import PhoneTextField from '../common/Form/PhoneTextField';
import { validateLogInFormWithMobile } from '../../utils/Validator/Login';
import PasswordField from '../common/Form/PasswordField';
import {
  logInWithPasswordAction,
  authModalInfoAction,
} from '../../store/authentication/action';
import {
  selectAuthModal, selectIsLoadingState,
  selectLoginState,
} from '../../store/authentication/selector';
import ModalInfo from '../common/Modal/ModalInfo';
import configuration from '../../configs/configuration';
import PhoneCode from '../common/Form/PhoneCode';
import useResponsive from '../../utils/responsive';
import { useLanguage } from '../../context/language/language';
import { setSnackBarWording } from '../../store/authentication/slices';
import urlResolver from '../../constant/urlPattern';
import { goToPath } from '../../utils/useLocationPath';
import { getImageURL } from '../../utils/getImageURL';
import { trackClickByEventName, TRACKING_EVENT_NAME } from '../../utils/eventTracking';
import { LoadingModal } from '../common';
import { selectCartPending } from '../../store/cart/selector';

const LotusLogo = getImageURL('/logo/lotus-logo-primary.svg');

const useStyles = makeStyles((theme) => ({
  modalTitle: {
    ...theme.custom.fonts.header3,
    ...(theme.custom.isTabletOrMobile && {
      fontSize: 18,
      marginLeft: 8,
    }),
  },
  closeButton: {
    backgroundColor: theme.custom.colors.grey75,
  },
  loginForm: {
    '& .MuiGrid-item': {
      textAlign: 'center',
    },
  },
  fullWidth: {
    width: '100%',
  },
  loginButton: {
    '&:disabled': {
      color: theme.custom.colors.white,
      backgroundColor: theme.custom.colors.green200,
    },
  },
}));

const GreyTextSpan = styled.span(({ theme }) => ({
  color: theme.colors.grey500,
}));

function LoginModalDialog({
  showLoginModal,
  setShowLoginModal,
  title,
  redirectUrl,
  registerUrl,
  resetPasswordUrl,
}) {
  const theme = useTheme();
  const classes = useStyles();
  const closeModal = () => setShowLoginModal(false);
  const isMY = configuration.siteCode === 'MY';
  const isTH = configuration.siteCode === 'TH';

  const { isTabletOrMobile, isTablet, isMobile } = useResponsive();

  const [backdropTimeout, setBackdropTimeout] = useState(500);

  const [mobilePhoneNo, setMobilePhoneNo] = useState('');
  const [password, setPassword] = useState('');
  const [phoneValidator, setPhoneValidator] = useState({
    value: false,
    message: '',
  });
  const [passwordValidator, setPasswordValidator] = useState({
    value: false,
    message: '',
  });
  const [canConfirm, setCanConfirm] = useState(false);
  const [isAuthNeeded, setAuthNeeded] = useState(false);
  const [countryCode, setCountryCode] = useState('+60');

  const dispatch = useDispatch();
  //const history = useHistory();
  const authModel = useSelector(selectAuthModal);
  const isLoggedIn = useSelector(selectLoginState);
  const isLoading = useSelector(selectIsLoadingState);
  const cartPending = useSelector(selectCartPending);
  const word = useLanguage();

  const modalStyle = {
    overflow: 'visible',
    outline: 'none',
    maxWidth: '40%',
    width: 480,
    top: '50%',
    height: 'auto',

    ...(isTablet && {
      maxWidth: '70%'
    }),

    ...(isMobile && {
      width: '100% !important',
      maxWidth: '100% !important',
      height: '100% !important',
      borderRadius: 0,
      display: 'flex',
    }),
  };

  const formChange = (name, setFn, value) => {
    let newValue = value;
    let isCanPreform;
    const mobileMobileValidateLength = isMY && countryCode === '+60'
      ? DEFAULT_PHONE_NUMBER_LENGTH
      : isTH
        ? DEFAULT_PHONE_NUMBER_LENGTH
        : MINIMAL_PHONE_NUMBER_VALIDATE_LENGTH;

    if (name === 'phoneNumber') {
      isCanPreform = validateLogInFormWithMobile(
        value,
        password,
        false,
        mobileMobileValidateLength,
      );
      if (!value) {
        setPhoneValidator({
          value: false,
          message: '',
        });
      }
      if (isMY) {
        isCanPreform = isCanPreform
          && validatePhoneNumber(value, countryCode, configuration.siteCode);
      } else {
        isCanPreform = isCanPreform && validateThaiNumber(value);
      }
    } else {
      isCanPreform = validateLogInFormWithMobile(
        mobilePhoneNo,
        value,
        false,
        mobileMobileValidateLength,
      );
      setPasswordValidator({
        value: false,
        message: '',
      });
      if (isMY) {
        isCanPreform = isCanPreform
          && validatePhoneNumber(
            mobilePhoneNo,
            countryCode,
            configuration.siteCode,
          );
      } else {
        isCanPreform = isCanPreform && validateThaiNumber(mobilePhoneNo);
      }
    }

    if (setFn instanceof Function) {
      setFn(newValue);
    }
    setCanConfirm(isCanPreform);
  };

  const verifySubmit = (sendProps = null, callFn = null) => {
    let shouldCallFn = true;
    let validateResult;
    if (isMY) {
      validateResult = validatePhoneNumber(
        mobilePhoneNo,
        countryCode,
        configuration.siteCode,
      );
    } else {
      validateResult = validateThaiNumber(mobilePhoneNo);
    }

    if (!validateResult) {
      setPhoneValidator({
        value: !validateResult,
        message: 'Incorect mobile number format',
      });
      shouldCallFn = false;
    } else {
      setPhoneValidator({
        value: !validateResult,
        message: '',
      });
    }

    if (shouldCallFn && callFn instanceof Function) {
      window.digitalData = {
        ...window.digitalData,
        user: {
          ..._.get(window, 'digitalData.user', {}),
          preLoginEvent: '1',
        }
      };
      const event = new CustomEvent('preLoginEvent', {
        detail: {
          ...window.digitalData.user,
          pagename: 'Login',
        }
      });
      document.dispatchEvent(event);
      callFn(sendProps);
    }
  };

  const onLoginClick = () => {
    const sendingProps = {
      mobile: isTH
        ? `66${mobilePhoneNo.slice(1)}`
        : `${countryCode.slice(1)}${mobilePhoneNo}`,
      password,
    };

    verifySubmit(sendingProps, () => {
      dispatch(
        logInWithPasswordAction({
          user: sendingProps.mobile,
          password: sendingProps.password,
        }),
      );
    });
  };

  const handleCountryCodeChange = (changeValue) => {
    setMobilePhoneNo('');
    setPhoneValidator({
      value: false,
      message: '',
    });
    setCountryCode(changeValue);
  };

  // This is to hide the fact that modal gets re-rendered on each keystroke
  useEffect(() => {
    if (showLoginModal) {
      setAuthNeeded(true);
      setTimeout(() => {
        setBackdropTimeout(0);
      }, 700);
    } else {
      setBackdropTimeout(500);
    }
  }, [showLoginModal]);

  useEffect(() => {
    if (isAuthNeeded && isLoggedIn) {
      if (_.isEmpty(redirectUrl)) {
        closeModal();
      } else {
        goToPath(redirectUrl); // change to use utils because it has persistor flush
        // window.location.href = redirectUrl;
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    dispatch(setSnackBarWording({
      message: {
        login: word('SnackBar.Noti.LogIn'),
        register: word('SnackBar.Noti.AccountCreateAndLogIn'),
        registerLoyalty: word("SnackBar.Noti.ActivateMyLotus's")
      }
    }));
  }, []);

  return (
    <CustomModal
      isOpen={showLoginModal}
      onClose={closeModal}
      customStyle={modalStyle}
      backdropProps={{ timeout: backdropTimeout }}
    >
      <Grid container style={{ width: '100%' }}>
        <Grid item sm={12} style={{ width: '100%', }}>
          <Box
            display="flex"
            alignItems="center"
            style={{
              ...(isMobile
                ? {
                  padding: '24px 12px',
                  justifyContent: 'flex-end',
                  flexDirection: 'row-reverse',
                }
                : { padding: 24, justifyContent: 'space-between' }),
              borderBottom: `1px solid ${theme.colors.grey100}`,
            }}
          >
            <Box className={classes.modalTitle}>
              {title || word('Login.Register.ReadyToOrder')}
            </Box>
            <Box>
              <IconButton
                id="login-modal-close"
                data-cta-name-en="Close"
                data-cta-name-th="ปิด"
                data-cta-type="Button"
                classes={{ root: classes.closeButton }}
                onClick={closeModal}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          sm={12}
          style={{
            ...(isMobile
              ? {
                display: 'flex',
                flex: 1,
                padding: '24px 12px',
                heightt: '100%',
              }
              : { padding: 24 }),
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            {...(isMobile && { flex: 1 })}
          >
            <Box pb={isMobile ? 4 : 2}>
              <img src={LotusLogo} />
            </Box>
            <Box width={isMobile ? '100%' : '85%'} pb={1}>
              <Grid container>
                {isMY && (
                  <Grid item xs={4}>
                    <PhoneCode
                      defaultValue={countryCode}
                      onChange={handleCountryCodeChange}
                    />
                  </Grid>
                )}
                <Grid item xs={isMY ? 8 : 12}>
                  <PhoneTextField
                    value={mobilePhoneNo}
                    maxLength={
                      isMY && countryCode === '+60'
                        ? MY_PHONE_NUMBER_LENGTH
                        : DEFAULT_PHONE_NUMBER_LENGTH
                    }
                    onChange={formChange.bind(
                      this,
                      'phoneNumber',
                      setMobilePhoneNo,
                    )}
                    onClear={() =>
                      formChange('phoneNumber', setMobilePhoneNo, '')}
                    onBlur={() => verifySubmit()}
                    onFocus={() =>
                      setPhoneValidator({
                        value: false,
                        message: '',
                      })}
                    error={phoneValidator.value}
                    errorMessage={phoneValidator.message}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box width={isMobile ? '100%' : '85%'} pb={1}>
              <PasswordField
                value={password}
                onChange={formChange.bind(this, 'password', setPassword)}
                error={passwordValidator.value}
                errorMessage={passwordValidator.message}
                maxLength={50}
                label={word('General.Password')}
              />
            </Box>
            <Box width={isMobile ? '100%' : '85%'} pb={2}>
              <Button
                id="login-modal-btn"
                data-cta-name-en="Login"
                data-cta-name-th="เข้าสู่ระบบ"
                data-cta-type="Button"
                className={`${classes.fullWidth} ${classes.loginButton}`}
                size="large"
                disabled={!canConfirm}
                onClick={onLoginClick}
              >
                {word('General.LogIn')}
              </Button>
            </Box>
            <Box marginTop={isMobile ? '65px' : 0} pb={1}>
              <Link
                id="login-modal-continue-guess"
                data-cta-name-en="Continue As Guess"
                data-cta-name-th="ยังคงเป็นแขกรับเชิญ"
                data-cta-type="Link"
                color="secondary"
                href={redirectUrl}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sessionStorage.setItem('isContinueAsGuest', true);
                  if (_.isEmpty(redirectUrl)) {
                    closeModal();
                  }
                }}
              >
                {word('Login.Register.Guest')}
              </Link>
            </Box>
            {isMobile && <Box flex={1} />}
            <Box>
              <GreyTextSpan>
                {`${word('Login.Register.NewCustomer')} `}
                {' '}
              </GreyTextSpan>
              <Link
                id="login-modal-resgister"
                data-cta-name-en="Register"
                data-cta-name-th="ลงทะเบียน"
                data-cta-type="Link"
                color="secondary"
                href={urlResolver('register')}
                onClick={() => {
                  trackClickByEventName(TRACKING_EVENT_NAME.LOGIN_TO_REGISTER);
                }}
              >
                {word('Login.Register')}
              </Link>
            </Box>
            <Box marginBottom={isMobile ? '21px' : 0}>
              <GreyTextSpan>{`${word('Password.Edit')} `}</GreyTextSpan>
              <Link
                id="login-modal-reset"
                data-cta-name-en="Reset"
                data-cta-name-th="รีเซ็ต"
                data-cta-type="Link"
                color="secondary"
                href={urlResolver('forgetPassword')}
              >
                {word('Login.Register.Password.Reset')}
              </Link>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <ModalInfo
        isOpen={authModel.status >= 400}
        title={word('LogIn.Error.Title')}
        description={word('LogIn.Error.Description')}
        labelText={word('General.Retry')}
        onClose={() => {
          dispatch(authModalInfoAction(false));
          setMobilePhoneNo('');
          setPassword('');
        }}
      />
      <LoadingModal open={isLoading || cartPending} />
    </CustomModal>
  );
}

export default React.memo(LoginModalDialog);
